import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { RaRecord } from "react-admin";
import { useNotify } from "react-admin";
import dayjs from "dayjs";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";

import type { TallyConfig } from "@/types/tally-config";
import { APP_DISPLAY_DATE } from "@/utils/date-format";
import {
  getDefaultNarrationOptions,
  getStoredNarrationOptions,
  getTallyNarrationFieldDefinitions,
  setStoredNarrationOptions,
  type TallyNarrationOptions,
} from "@/utils/tally-narration-options";
import {
  exportToTallyExcel,
  previewRowsToVoucherRows,
  TALLY_VOUCHER_HEADERS,
  type TallyVoucherRow as TallyVoucherRowType,
} from "@/utils/export-to-tally-excel";
import {
  auditPreviewRows,
  buildManualVoucherRows,
  buildTallyExportPreview,
  extractRentMissingHalls,
  filterExportRowsByIgnoredVouchers,
  listProblemVouchers,
  rentHallMappingOptions,
  type TallyExportModule,
  type TallyExportPreview,
  type TallyManualVoucherForm,
  type TallyPreviewRow,
  type TallyReceiptOverride,
  type TallySkippedReceipt,
} from "@/utils/tally-export-preview";

// assignPreviewIds is not exported - use inline in dialog
function assignPreviewIds(rows: TallyVoucherRowType[], prefix: string): TallyPreviewRow[] {
  return rows.map((row, index) => ({
    ...row,
    id: `manual-${prefix}-${index}-${Date.now()}`,
    rowIssues: [],
  }));
}

function formatPreviewDate(value: Date | string | null | undefined): string {
  if (value == null || value === "") return "";
  const d = dayjs(value);
  return d.isValid() ? d.format(APP_DISPLAY_DATE) : "";
}

function parsePreviewDateInput(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const d = dayjs(trimmed, [APP_DISPLAY_DATE, "YYYY-MM-DD", "DD-MM-YYYY"], true);
  return d.isValid() ? d.toDate() : null;
}

const PREVIEW_COLUMNS: Array<{
  key: keyof TallyPreviewRow | "costAmount";
  label: string;
  width?: number;
}> = [
  { key: "voucherDate", label: "Date", width: 110 },
  { key: "voucherNumber", label: "Receipt no.", width: 120 },
  { key: "ledgerName", label: "Ledger", width: 140 },
  { key: "amount", label: "Amount", width: 88 },
  { key: "drCr", label: "Dr/Cr", width: 72 },
  { key: "categoryName", label: "Cost category", width: 130 },
  { key: "costCentre", label: "Cost centre", width: 120 },
  { key: "costAmount", label: "Cost amt", width: 88 },
  { key: "ledgerNarration", label: "Ledger narr.", width: 120 },
];

const PREVIEW_PAGE_SIZE = 50;
const EMPTY_RECORDS: RaRecord[] = [];

function trimCell(value: unknown): string {
  return String(value ?? "").trim();
}

function firstRowForVoucherKey(
  rows: TallyPreviewRow[],
  voucherKey: string
): TallyPreviewRow | undefined {
  let currentKey = "";
  for (const row of rows) {
    if (trimCell(row.voucherNumber)) {
      currentKey = trimCell(row.voucherNumber);
    }
    const key = currentKey || row.id;
    if (key === voucherKey) {
      return row;
    }
  }
  return undefined;
}

function formatCellValue(
  row: TallyPreviewRow,
  key: (typeof PREVIEW_COLUMNS)[number]["key"]
): string {
  if (key === "voucherDate") return formatPreviewDate(row.voucherDate);
  if (key === "amount" || key === "costAmount") {
    const n = row[key];
    return n == null ? "" : String(n);
  }
  return String(row[key as keyof TallyPreviewRow] ?? "");
}

type TallyPreviewRowEditDialogProps = {
  row: TallyPreviewRow | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string, patch: Partial<TallyPreviewRow>) => void;
};

function TallyPreviewRowEditDialog({ row, open, onClose, onSave }: TallyPreviewRowEditDialogProps) {
  const [draft, setDraft] = useState<Partial<TallyPreviewRow>>({});

  useEffect(() => {
    if (open) setDraft({});
  }, [open, row?.id]);

  const active = row && open;
  const merged = active ? { ...row, ...draft } : null;

  const handleClose = () => {
    setDraft({});
    onClose();
  };

  if (!merged) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit voucher line</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
        <TextField
          label="Receipt no."
          size="small"
          value={merged.voucherNumber}
          onChange={(e) => setDraft((d) => ({ ...d, voucherNumber: e.target.value }))}
        />
        <TextField
          label={`Date (${APP_DISPLAY_DATE})`}
          size="small"
          value={formatPreviewDate(merged.voucherDate)}
          onChange={(e) =>
            setDraft((d) => ({ ...d, voucherDate: parsePreviewDateInput(e.target.value) }))
          }
        />
        <TextField
          label="Ledger"
          size="small"
          value={merged.ledgerName}
          onChange={(e) => setDraft((d) => ({ ...d, ledgerName: e.target.value }))}
        />
        <TextField
          label="Amount"
          size="small"
          type="number"
          value={merged.amount}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              amount: Math.round(Number(e.target.value)) || 0,
            }))
          }
        />
        <FormControl size="small">
          <InputLabel>Dr/Cr</InputLabel>
          <Select
            label="Dr/Cr"
            value={merged.drCr}
            onChange={(e) => setDraft((d) => ({ ...d, drCr: e.target.value as "Dr" | "Cr" }))}
          >
            <MenuItem value="Cr">Cr</MenuItem>
            <MenuItem value="Dr">Dr</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Cost category"
          size="small"
          value={merged.categoryName ?? ""}
          onChange={(e) => setDraft((d) => ({ ...d, categoryName: e.target.value }))}
        />
        <TextField
          label="Cost centre"
          size="small"
          value={merged.costCentre ?? ""}
          onChange={(e) => setDraft((d) => ({ ...d, costCentre: e.target.value }))}
        />
        <TextField
          label="Cost amount"
          size="small"
          type="number"
          value={merged.costAmount ?? ""}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              costAmount: Math.round(Number(e.target.value)) || 0,
            }))
          }
        />
        <TextField
          label="Ledger narration"
          size="small"
          value={merged.ledgerNarration ?? ""}
          onChange={(e) => setDraft((d) => ({ ...d, ledgerNarration: e.target.value }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(merged.id, draft);
            handleClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type TallyExportPreviewDialogCommonProps = {
  open: boolean;
  onClose: () => void;
  filenamePrefix: string;
  totalNote?: string;
  initialPreview: TallyExportPreview;
  /** Optional controls above the summary chips (e.g. bank receipt prefix / seed). */
  headerExtra?: ReactNode;
};

export type TallyExportPreviewDialogProps = TallyExportPreviewDialogCommonProps &
  (
    | {
        source?: "receipts";
        module: TallyExportModule;
        config: TallyConfig;
        records: RaRecord[];
      }
    | {
        source: "prebuilt";
        /** Green chip count (e.g. bank credits included). */
        includedCount: number;
        /** Noun after the count; default `"receipt(s)"`. */
        includedNoun?: string;
        manualFormDefaults?: Partial<TallyManualVoucherForm>;
      }
  );

function defaultManualForm(
  skipped: TallySkippedReceipt,
  config: TallyConfig | undefined,
  module: TallyExportModule | undefined,
  manualFormDefaults?: Partial<TallyManualVoucherForm>
): TallyManualVoucherForm {
  const base: TallyManualVoucherForm = {
    date: dayjs().format("YYYY-MM-DD"),
    paymentMode: "CASH",
    amount: 0,
    incomeLedger: "",
    drLedger: "",
    costCategory: "",
    costCentre: "",
    ledgerNarration: "",
    ...manualFormDefaults,
    // Always prefer an explicit default, otherwise the skipped row label.
    receiptNo: manualFormDefaults?.receiptNo ?? skipped.receiptNo,
  };
  if (!config || !module) {
    return base;
  }
  if (module === "lagat") {
    return {
      ...base,
      incomeLedger: config.lagat.incomeLedger,
      drLedger: config.lagat.cashLedger,
      costCategory: config.lagat.costCategory,
      costCentre: config.lagat.costCentre,
    };
  }
  if (module === "niyaaz") {
    return {
      ...base,
      incomeLedger: config.niyaaz.incomeLedger,
      drLedger: config.niyaaz.cashLedger,
      costCategory: config.niyaaz.costCategory,
      costCentre: config.niyaaz.costCentre,
    };
  }
  if (module === "yearlyNiyaaz") {
    return {
      ...base,
      incomeLedger: config.yearlyNiyaaz.incomeLedger,
      drLedger: config.yearlyNiyaaz.cashLedger,
      costCategory: config.yearlyNiyaaz.costCategory,
      costCentre: config.yearlyNiyaaz.costCentre,
    };
  }
  if (module === "fmb") {
    return {
      ...base,
      incomeLedger: config.fmb.annual.incomeLedger,
      drLedger: config.fmb.cashLedger,
      costCategory: config.fmb.annual.costCategory,
      costCentre: config.fmb.annual.costCentre,
    };
  }
  if (module === "sabil") {
    return {
      ...base,
      incomeLedger: config.sabil.CHULA.incomeLedger,
      drLedger: config.sabil.cashLedger,
      costCategory: config.sabil.costCategory,
      costCentre: config.sabil.CHULA.costCentre,
    };
  }
  const firstHall = Object.values(config.rent.halls)[0];
  return {
    ...base,
    incomeLedger: firstHall?.incomeLedger ?? "",
    drLedger: firstHall?.cashLedger ?? "",
    costCategory: firstHall?.costCategory ?? "",
    costCentre: firstHall?.costCentre ?? "",
  };
}

export default function TallyExportPreviewDialog(props: TallyExportPreviewDialogProps) {
  const { open, onClose, filenamePrefix, totalNote, initialPreview, headerExtra } = props;
  const isPrebuilt = props.source === "prebuilt";
  const module = isPrebuilt ? undefined : props.module;
  const config = isPrebuilt ? undefined : props.config;
  const records = isPrebuilt ? EMPTY_RECORDS : props.records;
  const includedNoun = isPrebuilt ? (props.includedNoun ?? "receipt(s)") : "receipt(s)";
  const manualFormDefaults = isPrebuilt ? props.manualFormDefaults : undefined;
  const prebuiltIncludedCount = isPrebuilt ? props.includedCount : undefined;

  const [rows, setRows] = useState<TallyPreviewRow[]>(initialPreview.rows);
  const [skipped, setSkipped] = useState<TallySkippedReceipt[]>(initialPreview.skipped);
  const [overrides, setOverrides] = useState<Record<string, TallyReceiptOverride>>({});
  const [hallMaps, setHallMaps] = useState<Record<string, Record<string, string>>>({});
  const [manualOpen, setManualOpen] = useState<Record<string, boolean>>({});
  const [manualForms, setManualForms] = useState<Record<string, TallyManualVoucherForm>>({});
  const [page, setPage] = useState(0);
  const [editingRow, setEditingRow] = useState<TallyPreviewRow | null>(null);
  const [ignoredVoucherKeys, setIgnoredVoucherKeys] = useState<Set<string>>(() => new Set());
  const [ignoredSkippedIds, setIgnoredSkippedIds] = useState<Set<string>>(() => new Set());
  const notify = useNotify();

  const hallOptions = useMemo(() => (config ? rentHallMappingOptions(config) : []), [config]);

  const narrationFieldDefs = useMemo(
    () => (module ? getTallyNarrationFieldDefinitions(module) : []),
    [module]
  );
  const [narrationOptions, setNarrationOptions] = useState<TallyNarrationOptions>(() =>
    module ? getStoredNarrationOptions(module) : {}
  );

  // Prebuilt sources (bank statement) rebuild voucher numbers externally; sync table when they change.
  useEffect(() => {
    if (!open) return;
    if (isPrebuilt) {
      setRows(initialPreview.rows);
      setSkipped(initialPreview.skipped);
      setOverrides({});
      setHallMaps({});
      setManualOpen({});
      setManualForms({});
      setPage(0);
      setEditingRow(null);
      setIgnoredVoucherKeys(new Set());
      setIgnoredSkippedIds(new Set());
    } else if (module) {
      const stored = getStoredNarrationOptions(module);
      setNarrationOptions(stored);
    }
  }, [isPrebuilt, open, initialPreview, module]);

  const auditedRows = useMemo(() => auditPreviewRows(rows), [rows]);
  const problemVouchers = useMemo(() => listProblemVouchers(auditedRows), [auditedRows]);
  const activeProblemVouchers = useMemo(
    () => problemVouchers.filter((v) => !ignoredVoucherKeys.has(v.key)),
    [problemVouchers, ignoredVoucherKeys]
  );
  const activeSkipped = useMemo(
    () => skipped.filter((s) => !ignoredSkippedIds.has(s.receiptId)),
    [skipped, ignoredSkippedIds]
  );
  const blocking = activeProblemVouchers.length > 0;
  const exportRows = useMemo(
    () => filterExportRowsByIgnoredVouchers(auditedRows, ignoredVoucherKeys),
    [auditedRows, ignoredVoucherKeys]
  );
  const pagedRows = useMemo(() => {
    const start = page * PREVIEW_PAGE_SIZE;
    return auditedRows.slice(start, start + PREVIEW_PAGE_SIZE);
  }, [auditedRows, page]);

  const includedChipCount = prebuiltIncludedCount ?? Math.max(0, records.length - skipped.length);

  const rebuild = useCallback(
    (
      nextOverrides: Record<string, TallyReceiptOverride>,
      nextNarrationOptions: TallyNarrationOptions = narrationOptions
    ) => {
      if (isPrebuilt || !module || !config) return;
      const preview = buildTallyExportPreview(
        module,
        records,
        config,
        nextOverrides,
        nextNarrationOptions
      );
      setRows(preview.rows);
      setSkipped(preview.skipped);
      setOverrides(nextOverrides);
      setNarrationOptions(nextNarrationOptions);
    },
    [isPrebuilt, module, records, config, narrationOptions]
  );

  const handleToggleField = (key: string, checked: boolean) => {
    if (!module) return;
    const next = { ...narrationOptions, [key]: checked };
    setStoredNarrationOptions(module, next);
    rebuild(overrides, next);
  };

  const handleSelectAllFields = () => {
    if (!module) return;
    const next: TallyNarrationOptions = {};
    for (const def of narrationFieldDefs) {
      next[def.key] = true;
    }
    setStoredNarrationOptions(module, next);
    rebuild(overrides, next);
  };

  const handleDeselectAllFields = () => {
    if (!module) return;
    const next: TallyNarrationOptions = {};
    for (const def of narrationFieldDefs) {
      next[def.key] = false;
    }
    setStoredNarrationOptions(module, next);
    rebuild(overrides, next);
  };

  const handleResetFieldDefaults = () => {
    if (!module) return;
    const defaults = getDefaultNarrationOptions(module);
    setStoredNarrationOptions(module, defaults);
    rebuild(overrides, defaults);
  };

  const updateRow = (id: string, patch: Partial<TallyPreviewRow>) => {
    setRows((prev) =>
      auditPreviewRows(prev.map((r) => (r.id === id ? { ...r, ...patch, rowIssues: [] } : r)))
    );
  };

  const applyHallMappings = (skippedReceipt: TallySkippedReceipt) => {
    const map = hallMaps[skippedReceipt.receiptId] ?? {};
    const nextOverrides = {
      ...overrides,
      [skippedReceipt.receiptId]: {
        ...overrides[skippedReceipt.receiptId],
        rentHallMap: { ...overrides[skippedReceipt.receiptId]?.rentHallMap, ...map },
      },
    };
    rebuild(nextOverrides, narrationOptions);
    setHallMaps((prev) => {
      const copy = { ...prev };
      delete copy[skippedReceipt.receiptId];
      return copy;
    });
  };

  const submitManual = (skippedReceipt: TallySkippedReceipt) => {
    const form = manualForms[skippedReceipt.receiptId];
    if (!form) return;
    const built = buildManualVoucherRows(form);
    if (built.length === 0) return;
    const newRows = assignPreviewIds(built, skippedReceipt.receiptId);
    setRows((prev) => auditPreviewRows([...prev, ...newRows]));
    setSkipped((prev) => prev.filter((s) => s.receiptId !== skippedReceipt.receiptId));
    setManualOpen((prev) => ({ ...prev, [skippedReceipt.receiptId]: false }));
  };

  const ignoreVoucher = (key: string) => {
    setIgnoredVoucherKeys((prev) => new Set(prev).add(key));
  };

  const ignoreSkippedReceipt = (receiptId: string) => {
    setIgnoredSkippedIds((prev) => new Set(prev).add(receiptId));
  };

  const handleDownload = () => {
    if (exportRows.length === 0) return;
    exportToTallyExcel(previewRowsToVoucherRows(exportRows), { filenamePrefix });
    const ignoredV = ignoredVoucherKeys.size;
    const ignoredS = ignoredSkippedIds.size;
    if (ignoredV > 0 || ignoredS > 0) {
      const parts: string[] = [];
      if (ignoredV > 0) parts.push(`${ignoredV} voucher(s) with issues`);
      if (ignoredS > 0) parts.push(`${ignoredS} receipt(s) not built`);
      notify(`Exported after ignoring: ${parts.join("; ")}.`, { type: "warning" });
    }
    onClose();
  };

  const handleReset = () => {
    const defaults = module ? getDefaultNarrationOptions(module) : {};
    if (module) {
      setStoredNarrationOptions(module, defaults);
      setNarrationOptions(defaults);
      if (config) {
        const preview = buildTallyExportPreview(module, records, config, {}, defaults);
        setRows(preview.rows);
        setSkipped(preview.skipped);
      } else {
        setRows(initialPreview.rows);
        setSkipped(initialPreview.skipped);
      }
    } else {
      setRows(initialPreview.rows);
      setSkipped(initialPreview.skipped);
    }
    setOverrides({});
    setHallMaps({});
    setManualOpen({});
    setManualForms({});
    setPage(0);
    setEditingRow(null);
    setIgnoredVoucherKeys(new Set());
    setIgnoredSkippedIds(new Set());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Tally export preview</DialogTitle>
      <DialogContent dividers>
        {headerExtra ? <Box sx={{ mb: 2 }}>{headerExtra}</Box> : null}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip label={`${auditedRows.length} voucher line(s)`} size="small" />
          <Chip
            label={`${includedChipCount} ${includedNoun} included`}
            size="small"
            color="success"
          />
          {activeSkipped.length > 0 ? (
            <Chip
              label={`${activeSkipped.length} ${isPrebuilt ? "row(s)" : "receipt(s)"} need attention`}
              size="small"
              color="warning"
            />
          ) : null}
          {activeProblemVouchers.length > 0 ? (
            <Chip
              label={`${activeProblemVouchers.length} voucher(s) with issues`}
              size="small"
              color="error"
            />
          ) : null}
        </Box>

        {totalNote ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {totalNote}
          </Alert>
        ) : null}

        {!isPrebuilt && module && narrationFieldDefs.length > 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Narration fields
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Choose which fields to include in export narrations
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button size="small" variant="text" onClick={handleSelectAllFields}>
                  Select all
                </Button>
                <Button size="small" variant="text" onClick={handleDeselectAllFields}>
                  Deselect all
                </Button>
                <Button size="small" variant="text" onClick={handleResetFieldDefaults}>
                  Reset defaults
                </Button>
              </Box>
            </Box>
            <FormGroup row sx={{ gap: { xs: 0.5, sm: 2 } }}>
              {narrationFieldDefs.map((def) => {
                const checked = narrationOptions[def.key] ?? def.defaultChecked;
                return (
                  <FormControlLabel
                    key={def.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => handleToggleField(def.key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{def.label}</Typography>}
                  />
                );
              })}
            </FormGroup>
          </Paper>
        ) : null}

        {activeProblemVouchers.length > 0 ? (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Vouchers with issues ({activeProblemVouchers.length}) — fix, edit a line, or ignore
            </Typography>
            <TableContainer sx={{ maxHeight: 220, mb: 2, border: 1, borderColor: "divider" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Receipt no.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Lines</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Issues</TableCell>
                    <TableCell sx={{ width: 100, fontWeight: 600 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeProblemVouchers.map((voucher) => {
                    const firstLine = firstRowForVoucherKey(auditedRows, voucher.key);
                    return (
                      <TableRow
                        key={`problem-v-${voucher.key}`}
                        sx={(theme) => ({ bgcolor: alpha(theme.palette.error.main, 0.08) })}
                      >
                        <TableCell>{voucher.voucherNumber}</TableCell>
                        <TableCell>{voucher.lineCount}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="error">
                            {voucher.issues.join(" ")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            {firstLine ? (
                              <IconButton
                                size="small"
                                aria-label="Edit first line"
                                onClick={() => setEditingRow(firstLine)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => ignoreVoucher(voucher.key)}
                            >
                              Ignore
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}

        <Typography variant="subtitle2" gutterBottom>
          All voucher lines ({TALLY_VOUCHER_HEADERS.length} Excel columns) — read-only preview,
          paginated
        </Typography>
        <TableContainer sx={{ maxHeight: 360, mb: 0, border: 1, borderColor: "divider" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                {PREVIEW_COLUMNS.map((col) => (
                  <TableCell key={col.key} sx={{ minWidth: col.width, fontWeight: 600 }}>
                    {col.label}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Issues</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={PREVIEW_COLUMNS.length + 2}>
                    <Typography variant="body2" color="text.secondary">
                      No voucher lines yet. Resolve skipped receipts below or adjust filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={(theme) => ({
                      ...(row.rowIssues.length > 0
                        ? { bgcolor: alpha(theme.palette.error.main, 0.08) }
                        : null),
                    })}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label="Edit line"
                        onClick={() => setEditingRow(row)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    {PREVIEW_COLUMNS.map((col) => (
                      <TableCell key={col.key} sx={{ py: 0.75 }}>
                        <Typography variant="body2" noWrap title={formatCellValue(row, col.key)}>
                          {formatCellValue(row, col.key)}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell>
                      {row.rowIssues.length > 0 ? (
                        <Typography variant="caption" color="error">
                          {row.rowIssues.join(" ")}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {auditedRows.length > PREVIEW_PAGE_SIZE ? (
          <TablePagination
            component="div"
            count={auditedRows.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={PREVIEW_PAGE_SIZE}
            rowsPerPageOptions={[PREVIEW_PAGE_SIZE]}
          />
        ) : null}

        <TallyPreviewRowEditDialog
          row={editingRow}
          open={editingRow != null}
          onClose={() => setEditingRow(null)}
          onSave={updateRow}
        />

        {activeSkipped.length > 0 ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              {isPrebuilt
                ? "Rows not exported — ignore or add manual lines"
                : "Receipts not exported — fix here, add manual lines, or ignore"}
            </Typography>
            {activeSkipped.map((item) => {
              const missingHalls = module === "rent" ? extractRentMissingHalls(item.reasons) : [];
              const map = hallMaps[item.receiptId] ?? {};
              const manual = manualForms[item.receiptId];
              const showManual = manualOpen[item.receiptId];

              return (
                <Box
                  key={item.receiptId}
                  sx={(theme) => ({
                    mb: 2,
                    p: 2,
                    border: 1,
                    borderColor: "warning.main",
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.warning.main, 0.06),
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {item.receiptNo}
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => ignoreSkippedReceipt(item.receiptId)}
                    >
                      Ignore
                    </Button>
                  </Box>
                  {item.reasons.map((reason) => (
                    <Typography key={reason} variant="body2" color="text.secondary">
                      {reason}
                    </Typography>
                  ))}

                  {module === "rent" && missingHalls.length > 0 && hallOptions.length > 0 ? (
                    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                      {missingHalls.map((hall) => (
                        <Autocomplete
                          key={hall}
                          size="small"
                          options={hallOptions}
                          value={map[hall] ?? null}
                          onChange={(_, value) =>
                            setHallMaps((prev) => ({
                              ...prev,
                              [item.receiptId]: { ...prev[item.receiptId], [hall]: value ?? "" },
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={`Map “${hall}” to Tally hall`}
                              placeholder="Select configured hall"
                            />
                          )}
                        />
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => applyHallMappings(item)}
                        disabled={missingHalls.some((h) => !map[h]?.trim())}
                      >
                        Apply hall mapping & rebuild
                      </Button>
                    </Box>
                  ) : null}

                  <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setManualOpen((prev) => ({ ...prev, [item.receiptId]: !showManual }));
                        if (!manualForms[item.receiptId]) {
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: defaultManualForm(
                              item,
                              config,
                              module,
                              manualFormDefaults
                            ),
                          }));
                        }
                      }}
                    >
                      {showManual ? "Hide manual entry" : "Add manual voucher lines"}
                    </Button>
                  </Box>

                  {showManual && manual ? (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        label="Receipt no."
                        size="small"
                        value={manual.receiptNo}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: { ...manual, receiptNo: e.target.value },
                          }))
                        }
                      />
                      <TextField
                        label="Date (YYYY-MM-DD)"
                        size="small"
                        value={manual.date}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: { ...manual, date: e.target.value },
                          }))
                        }
                      />
                      <FormControl size="small">
                        <InputLabel>Payment mode</InputLabel>
                        <Select
                          label="Payment mode"
                          value={manual.paymentMode}
                          onChange={(e) =>
                            setManualForms((prev) => ({
                              ...prev,
                              [item.receiptId]: { ...manual, paymentMode: e.target.value },
                            }))
                          }
                        >
                          <MenuItem value="CASH">CASH</MenuItem>
                          <MenuItem value="ONLINE">ONLINE</MenuItem>
                          <MenuItem value="CHEQUE">CHEQUE</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Amount"
                        size="small"
                        type="number"
                        value={manual.amount}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: {
                              ...manual,
                              amount: Math.round(Number(e.target.value)),
                            },
                          }))
                        }
                      />
                      <TextField
                        label="Income ledger (Cr)"
                        size="small"
                        value={manual.incomeLedger}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: { ...manual, incomeLedger: e.target.value },
                          }))
                        }
                      />
                      <TextField
                        label="Cash / bank ledger (Dr)"
                        size="small"
                        value={manual.drLedger}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: { ...manual, drLedger: e.target.value },
                          }))
                        }
                      />
                      <TextField
                        label="Cost category"
                        size="small"
                        value={manual.costCategory}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: { ...manual, costCategory: e.target.value },
                          }))
                        }
                      />
                      <TextField
                        label="Cost centre"
                        size="small"
                        value={manual.costCentre}
                        onChange={(e) =>
                          setManualForms((prev) => ({
                            ...prev,
                            [item.receiptId]: { ...manual, costCentre: e.target.value },
                          }))
                        }
                      />
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ gridColumn: { sm: "1 / -1" }, justifySelf: "start" }}
                        onClick={() => submitManual(item)}
                        disabled={!manual.incomeLedger.trim() || !manual.drLedger.trim()}
                      >
                        Add lines to export
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </>
        ) : null}

        {blocking ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            Fix or ignore each voucher with issues above before downloading.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset}>Reset preview</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={exportRows.length === 0 || blocking}
        >
          Download Excel
          {exportRows.length < auditedRows.length ? ` (${exportRows.length} lines)` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
